import React, { useEffect, useState } from 'react';
import { CardHeader, CardSection } from '@europrocurement/flexy-components';
import {
    CATEGORY_LEGAL_ANNOUCEMENT,
    type DossierArticle,
    RequiredDocumentsList,
    RequiredDocumentUploader,
    useCurrentDossierCtx,
    useDocumentPreviewContext,
    useDocumentSign,
    useDocumentSignRender,
    useDossierArticle,
    useDossierDocumentsCtx,
    useDossierDocumentSignContext,
    useDossierLegalAnnoucements,
    useDossierProcessesActionsAndRender,
    VALIDATOR_TYPE_LEGAL_ANNOUCEMENT,
} from '@europrocurement/l2d-modules';
import useDossierFile from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useDossierFile';
import { DOC_TYPE_DOWNLOADABLE } from '@europrocurement/l2d-domain';
import DownloadableDocumentsList from '@europrocurement/l2d-modules/modules/Dossiers/components/detail/DownloadableDocumentsList/DownloadableDocumentsList';
import { DisplayedDocument } from '@europrocurement/l2d-modules/modules/Dossiers/providers/types';
import { LegalAnnoucement } from '@europrocurement/l2d-modules/modules/Dossiers/types';

const DownloadableDocumentsTab: React.FunctionComponent<React.PropsWithChildren> = function ({
    children,
}) {
    const [listItems, setListItems] = useState<Array<DisplayedDocument>>([]);
    const [externalListItems, setExternalListItems] = useState<Array<DisplayedDocument>>([]);
    const [legalAnnoucementsLoaded, setLegalAnnoucementsLoaded] = useState<boolean>();
    const [legalAnnoucements, setLegalAnnoucements] = useState<Array<LegalAnnoucement>>([]);
    const { dosDocs, getDossierDocumentsByCategory } = useDossierDocumentsCtx();
    const [prestaArticles, setPrestaArticles] = useState<DossierArticle[]>();
    const { dossier } = useCurrentDossierCtx();
    const { getArticles } = useDossierArticle();
    const {
        handleAddFile,
        handleRemoveFile,
        handleShow,
        handleDownload,
        handleMoveFile,
        handleAdditionalUploads,
        submittingItemId,
    } = useDossierFile({
        typeDoc: DOC_TYPE_DOWNLOADABLE,
        displayPreviewOnAddFile: false,
    });

    const isSubmitting = submittingItemId !== null;

    const { handleSignSingleDocument } = useDocumentSign();
    const { showSignedDocument } = useDocumentSignRender();
    const { getDossiersBySignedFileId, getSignDocItems } = useDossierDocumentSignContext();

    const handleSignSingleDocProxy = (label: string, fichierId: number, fileName?: string) => {
        if (!dossier) {
            return;
        }
        handleSignSingleDocument(
            dossier.id as number,
            dossier.prescripteurId as number,
            dossier.prescripteurLibelle as string,
            dossier.prescripteurEmail as string,
            label,
            fichierId,
            fileName,
        );
    };

    const handleDownloadSigned = (fichierId: number, fileName: string) => {
        showSignedDocument(fichierId, fileName, getDossiersBySignedFileId(fichierId));
    };

    const { loadLegalAnnoucements } = useDossierProcessesActionsAndRender();
    const { getLegalAnnoucementFileContent } = useDossierLegalAnnoucements();
    const { setDocumentPreview } = useDocumentPreviewContext();

    const handleShowExternalUrl = async (urlOrSentinel: string) => {
        if (urlOrSentinel.startsWith('attestation:')) {
            const [, rawId, numero] = urlOrSentinel.split(':');
            const blob = await getLegalAnnoucementFileContent(Number(rawId));
            setDocumentPreview({ fileName: numero, fileType: 'pdf', content: blob });
            return;
        }
        window.open(urlOrSentinel, '_blank');
    };

    useEffect(() => {
        if (!dossier) {
            return;
        }
        if (!legalAnnoucementsLoaded) {
            setLegalAnnoucementsLoaded(true);
            loadLegalAnnoucements(dossier, CATEGORY_LEGAL_ANNOUCEMENT, [
                VALIDATOR_TYPE_LEGAL_ANNOUCEMENT,
            ])
                .then((items: LegalAnnoucement[]) => {
                    setLegalAnnoucements(items);
                })
                .catch(() => {
                    setLegalAnnoucements([]);
                });
        }

        getArticles(dossier.id as number, CATEGORY_LEGAL_ANNOUCEMENT).then(
            (res: DossierArticle[]) => setPrestaArticles(res),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [legalAnnoucementsLoaded]);

    useEffect(() => {
        if (!dossier) {
            return;
        }

        const downloadableDocs = [
            ...getDossierDocumentsByCategory({
                categoryId: DOC_TYPE_DOWNLOADABLE,
                dossierId: dossier?.id as number,
            }),
            ...getDossierDocumentsByCategory({
                categoryId: DOC_TYPE_DOWNLOADABLE,
                additionalDocs: true,
                dossierId: dossier?.id as number,
            }),
        ];

        const downloadableFichierIds = new Set(
            downloadableDocs.map((d) => d.fichierId).filter((id): id is number => id != null),
        );

        const signDocItems = getSignDocItems({
            referenceFichierIds: downloadableFichierIds,
            onlySigned: true,
        });
        setListItems([...downloadableDocs, ...signDocItems]);

        let externalDocLegal: DisplayedDocument[] = [];

        legalAnnoucements.map((legal) => {
            const validatorProcess = dossier.validationProcesses.find(
                (process) => process.identifier === legal.id.toString(),
            );
            if (validatorProcess) {
                const prestaArticle = (prestaArticles || []).find((article) =>
                    validatorProcess.articles.includes(article.id),
                );

                if (prestaArticle) {
                    const baseDoc = {
                        dossierId: dossier.id as number,
                        downloadable: false,
                        uploadable: false,
                        uploading: false,
                        editable: false,
                        showable: true,
                        signable: true,
                        canDownloadSigned: false,
                        removable: false,
                        removableDoc: false,
                        hasType: false,
                        movable: false,
                        showStatus: false,
                    };

                    if (legal.justif) {
                        externalDocLegal = [
                            ...externalDocLegal,
                            {
                                ...baseDoc,
                                fileName: legal.justif,
                                label: `Justificatif ${validatorProcess.validatorLabel} ${legal.numero}` as string,
                                id: legal.id,
                            },
                        ];
                    }

                    if (legal.idDocumentAttestation) {
                        externalDocLegal = [
                            ...externalDocLegal,
                            {
                                ...baseDoc,
                                fileName: `attestation:${legal.id}:${legal.numero}`,
                                label: `Attestation ${validatorProcess.validatorLabel} ${legal.numero}` as string,
                                id: legal.id,
                            },
                        ];
                    }
                }
            }
            return true;
        });

        setExternalListItems(externalDocLegal);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dosDocs, legalAnnoucements, prestaArticles, getDossiersBySignedFileId, getSignDocItems]);

    return (
        <CardSection
            sx={{ width: '100%' }}
            header={
                <CardHeader
                    title="Documents remis"
                    titleSx={{
                        fontSize: '20px',
                        lineHeight: '28px',
                    }}
                    border={false}
                />
            }
            sxContent={{
                padding: '8px 8px 32px',
                display: 'flex',
                flexDirection: 'column',
                gap: '24px 0',
                width: '100%',
            }}
        >
            {externalListItems.length > 0 && (
                <DownloadableDocumentsList
                    documents={externalListItems}
                    onDownload={handleDownload}
                    onShow={handleShowExternalUrl}
                />
            )}
            <RequiredDocumentsList
                documents={listItems}
                onUpload={handleAddFile}
                onDeletion={handleRemoveFile}
                onDocDeletion={() => {}}
                onDownload={handleDownload}
                onShow={handleShow}
                onSign={handleSignSingleDocProxy}
                onDownloadSigned={handleDownloadSigned}
                onMove={handleMoveFile}
                isSubmitting={isSubmitting}
                submittingItemId={submittingItemId}
            />
            <RequiredDocumentUploader
                uploading={submittingItemId === 'uploader'}
                disabled={isSubmitting}
                onChange={handleAdditionalUploads}
            />
            {children}
        </CardSection>
    );
};

export default DownloadableDocumentsTab;
